import { useCssTokens } from '../../hooks/useCssTokens';

const paletteSteps = [50, 100, 200, 300, 400, 500, 600, 700, 800, 900];
const neutralSteps = [0, 50, 100, 200, 300, 400, 500, 600, 700, 800, 900, 950];

const paletteGroups = [
  { name: 'Brand', key: 'brand', steps: paletteSteps },
  { name: 'Neutral', key: 'neutral', steps: neutralSteps },
  { name: 'Success', key: 'success', steps: paletteSteps },
  { name: 'Warning', key: 'warning', steps: paletteSteps },
  { name: 'Error', key: 'error', steps: paletteSteps },
  { name: 'Info', key: 'info', steps: paletteSteps },
  { name: 'Accent', key: 'accent', steps: paletteSteps },
];

const semanticGroups = [
  {
    name: 'Text',
    tokens: ['text', 'text-muted', 'text-inverse', 'text-disabled'],
  },
  {
    name: 'Surfaces',
    tokens: ['surface-page', 'surface-card', 'surface-elevated', 'surface-overlay'],
  },
  {
    name: 'Borders',
    tokens: ['border-default', 'border-strong', 'border-focus'],
  },
  {
    name: 'Actions',
    tokens: [
      'action-primary',
      'action-primary-hover',
      'action-primary-pressed',
      'action-secondary',
      'action-secondary-hover',
      'action-secondary-pressed',
      'action-destructive',
      'action-destructive-hover',
      'action-destructive-pressed',
    ],
  },
  {
    name: 'Feedback',
    tokens: ['status-success', 'status-warning', 'status-error', 'status-info'],
  },
  {
    name: 'Utility',
    tokens: ['focus-ring', 'scrim', 'shadow-color'],
  },
];

const paletteTokens = paletteGroups.flatMap((group) =>
  group.steps.map((step) => `${group.key}-${step}`),
);
const semanticTokens = semanticGroups.flatMap((group) => group.tokens);
const allTokens = [...paletteTokens, ...semanticTokens];

function getLightness(value: string) {
  const parts = value.split(' ');
  const lightness = parts[2];
  if (!lightness) return null;
  const parsed = Number.parseFloat(lightness.replace('%', ''));
  return Number.isFinite(parsed) ? parsed : null;
}

function resolveTokenValue(value: string, values: Record<string, string>, depth = 0): string {
  if (depth > 3) return value;
  const match = value.match(/var\\(--([\\w-]+)\\)/);
  if (!match) return value;
  const tokenValue = values[match[1]];
  if (!tokenValue) return value;
  return resolveTokenValue(tokenValue, values, depth + 1);
}

function SwatchLabel({
  label,
  value,
  inverse,
}: {
  label: string;
  value?: string;
  inverse: boolean;
}) {
  return (
    <div className={inverse ? 'text-white' : 'text-foreground'}>
      <p className="text-xs font-semibold">{label}</p>
      <p className={inverse ? 'text-[11px] text-white/70' : 'text-[11px] text-muted-foreground'}>
        {value || '—'}
      </p>
    </div>
  );
}

export function ColorsShowcase() {
  const values = useCssTokens(allTokens);

  return (
    <div className="space-y-8">
      <div className="space-y-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            Base Palette
          </p>
          <p className="text-sm text-muted-foreground">
            Core hues with full scales for brand, neutral, and status colors.
          </p>
        </div>
        <div className="space-y-6">
          {paletteGroups.map((group) => (
            <div key={group.key} className="space-y-3">
              <h4 className="text-sm font-semibold text-foreground">{group.name}</h4>
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-5 lg:grid-cols-10">
                {group.steps.map((step) => {
                  const token = `${group.key}-${step}`;
                  const value = resolveTokenValue(values[token] ?? '', values);
                  const lightness = value ? getLightness(value) : null;
                  const inverse = lightness !== null ? lightness < 60 : false;
                  return (
                    <div
                      key={token}
                      className="aspect-square rounded-xl border border-border/40 p-2"
                      style={{ backgroundColor: `hsl(var(--${token}))` }}
                    >
                      <SwatchLabel label={String(step)} value={value} inverse={inverse} />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground">
            Semantic Roles
          </p>
          <p className="text-sm text-muted-foreground">
            Role-based tokens that components consume across light and dark themes.
          </p>
        </div>
        <div className="space-y-6">
          {semanticGroups.map((group) => (
            <div key={group.name} className="space-y-3">
              <h4 className="text-sm font-semibold text-foreground">{group.name}</h4>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                {group.tokens.map((token) => {
                  const value = resolveTokenValue(values[token] ?? '', values);
                  const lightness = value ? getLightness(value) : null;
                  const inverse = lightness !== null ? lightness < 60 : false;
                  return (
                    <div
                      key={token}
                      className="rounded-xl border border-border/40 p-3"
                      style={{ backgroundColor: `hsl(var(--${token}))` }}
                    >
                      <SwatchLabel label={token} value={value} inverse={inverse} />
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
